import { db, functions } from './firebase'; // Ensure functions is exported
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  collectionGroup,
  doc,
  deleteDoc,
  onSnapshot,
  query,
  updateDoc,
  where,
  orderBy,
  writeBatch,
  Timestamp,
  DocumentSnapshot,
  QuerySnapshot,
  getDoc,
  getDocs,
  FirestoreError,
} from 'firebase/firestore';
import { eachDayOfInterval, format, parseISO } from 'date-fns';
import {
  AvailabilitySummary,
  type CreateEventInput,
  Event,
  Participant,
  Preferences,
  Recommendation,
  type StopReadiness,
  User,
} from '@planacle/shared';

const EVENTS_COLLECTION = 'events';

/**
 * M-336: expand a `[start, end]` reopen range into the inclusive list of discrete ISO days it
 * covers. The availability pipeline treats `event.date` as a list of candidate days, so a range
 * must be materialised — otherwise only the two endpoints were ever evaluated.
 */
export const expandDateRange = (start: string, end: string): string[] => {
  const s = parseISO(start);
  const e = parseISO(end);
  // Invalid/reversed input → empty, which lets the flexible upcoming-days fallback take over
  // rather than persisting garbage candidate dates. Callers validate via validateReopenDates.
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return [];
  return eachDayOfInterval({ start: s, end: e }).map((d) => format(d, 'yyyy-MM-dd'));
};

/**
 * M-475: Creates a new event via the server-authoritative createEventCallable CF.
 * All server-owned fields (hostId, status, timestamps, participantCount) are set
 * server-side. The host participant is created atomically with the event.
 */
export const createEvent = async (input: CreateEventInput): Promise<string> => {
  try {
    const callable = httpsCallable<CreateEventInput, { eventId: string }>(
      functions,
      'createEventCallable'
    );
    const result = await callable(input);
    return result.data.eventId;
  } catch (error) {
    console.error('Error creating event: ', error);
    throw error;
  }
};

/**
 * Joins an event as a participant.
 *
 * H-301: This now calls the `joinEventCallable` Cloud Function instead of writing the
 * participant doc directly from the client. The CF enforces capacity (`expectedParticipants`)
 * and the voting-window guard transactionally — capabilities a client-side write cannot
 * provide because Firestore rules cannot read sibling subcollection size atomically and
 * a determined caller could bypass any client check.
 *
 * Firestore rules deny direct `create` on the participants subcollection — only this CF
 * (via Admin SDK) can add new participants.
 */
export const joinEvent = async (
  eventId: string,
  user: Partial<User> & { uid: string; role?: 'host' | 'guest'; guestAvailability?: string[] },
  // H-424: extra candidate dates the guest picked during onboarding beyond the event's
  // current ones — the CF unions them into `event.participantDates` (consensus input).
  proposedDates?: string[]
): Promise<void> => {
  try {
    const callable = httpsCallable<
      {
        eventId: string;
        displayName: string;
        role?: 'host' | 'guest';
        guestAvailability?: string[];
        proposedDates?: string[];
        email?: string;
        photoURL?: string;
      },
      { success: true }
    >(functions, 'joinEventCallable');
    await callable({
      eventId,
      displayName: user.displayName || 'Anonymous Guest',
      role: user.role,
      guestAvailability: user.guestAvailability,
      proposedDates: proposedDates && proposedDates.length > 0 ? proposedDates : undefined,
      email: user.email || undefined,
      photoURL: user.photoURL || undefined,
    });
  } catch (error) {
    console.error('Error joining event: ', error);
    throw error;
  }
};

export type SubmittedPreferences = Omit<Preferences, 'availability'>;

/**
 * Submits event-scoped preferences. Availability is persisted separately on the user document.
 */
export const submitPreferences = async (
  eventId: string,
  _userId: string,
  preferences: SubmittedPreferences,
  // H-424: extra candidate dates the user picked in the room editor beyond the event's
  // current ones — the CF unions them into `event.participantDates` (consensus input).
  proposedDates?: string[]
): Promise<void> => {
  try {
    const submitFn = httpsCallable(functions, 'submitEventPreferences');
    await submitFn({
      eventId,
      preferences,
      proposedDates: proposedDates && proposedDates.length > 0 ? proposedDates : undefined,
    });

    if (import.meta.env.VITE_USE_EMULATORS === 'true') {
      const recomputeFn = httpsCallable(functions, 'triggerRecompute');
      recomputeFn({ eventId }).catch(() => {
        // Recompute failed or functions emulator not running — triggers fire in production.
      });
    }
  } catch (error) {
    console.error('Error submitting preferences: ', error);
    throw error;
  }
};

/**
 * Real-time listener for Event Data
 */
export const subscribeToEvent = (
  eventId: string,
  callback: (event: Event | null) => void,
  onError?: (error: FirestoreError) => void
) => {
  return onSnapshot(
    doc(db, EVENTS_COLLECTION, eventId),
    (snap: DocumentSnapshot) => {
      callback(snap.exists() ? ({ id: snap.id, ...snap.data() } as Event) : null);
    },
    (error: FirestoreError) => {
      console.error('[subscribeToEvent] snapshot error', error.code, error.message);
      onError?.(error);
    }
  );
};

/**
 * H-420: public plan summary — the world-readable, presentation-only doc materialized
 * server-side at `events/{id}/public/summary` when the plan generates. Client mirror of
 * `functions/src/services/publicSummaryService.ts` (source of truth for the shape).
 */
export interface PublicPlanStop {
  time: string;
  name: string;
  image?: string | null;
  rating?: number | null;
  userRatingCount?: number | null;
  cost?: string | null;
  travelTime?: string | null;
  googleMapsUri?: string | null;
  websiteUri?: string | null;
  readiness?: StopReadiness;
}

export interface PublicPlanSummary {
  eventId: string;
  eventName: string;
  hostFirstName: string;
  participantCount: number;
  planTitle: string;
  narrative: string;
  date?: string;
  startTime?: string;
  endTime?: string;
  stops: PublicPlanStop[];
}

/**
 * One-shot read of the public plan summary. Returns null when the doc doesn't exist
 * (event pre-dates H-420, or the plan hasn't generated). Works signed-out — rules
 * allow public read on this path only.
 */
export const getPublicPlanSummary = async (eventId: string): Promise<PublicPlanSummary | null> => {
  const snap = await getDoc(doc(db, EVENTS_COLLECTION, eventId, 'public', 'summary'));
  return snap.exists() ? (snap.data() as PublicPlanSummary) : null;
};

/**
 * M-425: one-shot participants read — plan social proof on PlanSummary needs the
 * roster + ballots once, not a live subscription.
 */
export const getEventParticipants = async (eventId: string): Promise<Participant[]> => {
  const snapshot = await getDocs(collection(db, EVENTS_COLLECTION, eventId, 'participants'));
  return snapshot.docs.map((d) => ({ ...d.data(), uid: d.id }) as Participant);
};

/**
 * Real-time listener for Participants
 */
export const subscribeToParticipants = (
  eventId: string,
  callback: (participants: Participant[]) => void,
  onError?: (error: FirestoreError) => void
) => {
  return onSnapshot(
    collection(db, EVENTS_COLLECTION, eventId, 'participants'),
    (snapshot: QuerySnapshot) => {
      const participants: Participant[] = [];
      snapshot.forEach((doc) => {
        participants.push({ ...doc.data(), uid: doc.id } as Participant);
      });
      callback(participants);
    },
    (error: FirestoreError) => {
      console.error('[subscribeToParticipants] snapshot error', error.code, error.message);
      onError?.(error);
    }
  );
};

/**
 * Single-doc listener for the caller's own participant entry.
 *
 * H-254: Used in pre-join contexts (GuestOnboarding) where the user is not yet a
 * participant. A collection-level listener (`subscribeToParticipants`) would trigger
 * `permission-denied` on the LIST query — and the resulting unhandled snapshot error
 * has been observed to crash the Firestore SDK with `INTERNAL ASSERTION FAILED (ID:
 * ca9)`, which then prevents the subsequent `joinEvent` write from succeeding.
 *
 * The Firestore rule for `events/{id}/participants/{participantId}` allows
 * `request.auth.uid == participantId`, so a doc-level listener on the user's own UID
 * never trips a permission error — even when the doc doesn't yet exist.
 */
export const subscribeToMyParticipant = (
  eventId: string,
  userId: string,
  callback: (participant: Participant | null) => void,
  onError?: (error: FirestoreError) => void
) => {
  return onSnapshot(
    doc(db, EVENTS_COLLECTION, eventId, 'participants', userId),
    (snap: DocumentSnapshot) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      callback({ ...snap.data(), uid: snap.id } as Participant);
    },
    (error: FirestoreError) => {
      console.error('[subscribeToMyParticipant] snapshot error', error.code, error.message);
      onError?.(error);
    }
  );
};

/**
 * Real-time listener for Availability Summary
 */
export const subscribeToAvailabilitySummary = (
  eventId: string,
  callback: (summary: AvailabilitySummary | null) => void,
  onError?: (error: FirestoreError) => void
) => {
  return onSnapshot(
    doc(db, EVENTS_COLLECTION, eventId, 'availabilitySummary', 'summary'),
    (snap: DocumentSnapshot) => {
      callback(snap.exists() ? (snap.data() as AvailabilitySummary) : null);
    },
    (error: FirestoreError) => {
      console.error('[subscribeToAvailabilitySummary] snapshot error', error.code, error.message);
      onError?.(error);
    }
  );
};

/**
 * L-123: Route ranked ballot submission through the server-authoritative submitBallot
 * callable. The CF validates uniqueness, event status, deadline, and participant
 * membership before writing via Admin SDK — direct Firestore writes are denied by rules.
 * The userId param is kept for API stability; the CF derives identity from the auth token.
 */
export const voteForCandidates = async (
  eventId: string,
  _userId: string,
  ids: string[]
): Promise<void> => {
  try {
    const fn = httpsCallable<{ eventId: string; candidateVotes: string[] }, { success: boolean }>(
      functions,
      'submitBallot'
    );
    await fn({ eventId, candidateVotes: ids });
  } catch (error) {
    console.error('[voteForCandidates] submitBallot CF failed:', error);
    throw error;
  }
};

/**
 * Saves complementary (activity) votes to the participant document. Complementary
 * voting is still gated by Firestore rules (complementary_ready status + list shape).
 */
export const voteForComplementary = async (
  eventId: string,
  userId: string,
  ids: string[]
): Promise<void> => {
  try {
    const participantRef = doc(db, EVENTS_COLLECTION, eventId, 'participants', userId);
    await updateDoc(participantRef, { complementaryVotes: ids });
  } catch (error) {
    console.error('[voteForComplementary] failed to save complementaryVotes:', error);
    throw error;
  }
};

/**
 * Updates an existing event.
 */
export const updateEvent = async (eventId: string, data: Partial<Event>): Promise<void> => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    await updateDoc(eventRef, data);
  } catch (error) {
    console.error('Error updating event: ', error);
    throw error;
  }
};

/**
 * Removes the current user from an event (leave flow).
 * C-130: runs through the `leaveEvent` CF — Firestore rules deny all client-side
 * participant deletes (`allow delete: if false`), so the old direct `deleteDoc`
 * could never succeed. The CF derives the leaver from the auth token.
 */
export const leaveEvent = async (eventId: string): Promise<void> => {
  try {
    const fn = httpsCallable<{ eventId: string }, { success: boolean }>(functions, 'leaveEvent');
    await fn({ eventId });
  } catch (error) {
    console.error('[leaveEvent] failed:', error);
    throw error;
  }
};

/**
 * Removes a guest from an event (host only).
 * M-443: host-initiated counterpart of leaveEvent — Firestore rules deny all
 * client-side participant deletes, so removal runs through the CF.
 */
export const removeParticipant = async (eventId: string, participantUid: string): Promise<void> => {
  try {
    const fn = httpsCallable<{ eventId: string; participantUid: string }, { success: boolean }>(
      functions,
      'removeParticipant'
    );
    await fn({ eventId, participantUid });
  } catch (error) {
    console.error('[removeParticipant] failed:', error);
    throw error;
  }
};

/**
 * Updates host-editable event settings (currently the expected group size).
 * M-443: guests get no event-doc writes from the rules, so the host check and
 * the whitelist of editable fields both live server-side in the CF.
 */
export const updateEventSettings = async (
  eventId: string,
  settings: { expectedParticipants: number }
): Promise<void> => {
  try {
    const fn = httpsCallable<
      { eventId: string; expectedParticipants: number },
      { success: boolean }
    >(functions, 'updateEventSettings');
    await fn({ eventId, expectedParticipants: settings.expectedParticipants });
  } catch (error) {
    console.error('[updateEventSettings] failed:', error);
    throw error;
  }
};

/**
 * Deletes an event and all its subcollections (host only).
 * C-130: runs through the `deleteEvent` CF — the events rules grant no client
 * delete, so the old direct `deleteDoc` sweep always failed with permission-denied.
 */
export const deleteEvent = async (eventId: string): Promise<void> => {
  try {
    const fn = httpsCallable<{ eventId: string }, { success: boolean }>(functions, 'deleteEvent');
    await fn({ eventId });
  } catch (error) {
    console.error('[deleteEvent] failed:', error);
    throw error;
  }
};

/**
 * Updates the event with AI recommendations
 */
export const saveRecommendations = async (
  eventId: string,
  recommendations: Recommendation[]
): Promise<void> => {
  try {
    const eventRef = doc(db, EVENTS_COLLECTION, eventId);
    await updateDoc(eventRef, {
      recommendations: recommendations,
      status: 'generated',
    });
  } catch (error) {
    console.error('Error saving recommendations: ', error);
    throw error;
  }
};

/**
 * Checks if all participants have voted/joined and triggers AI if needed
 */
export const checkAndTriggerAI = async (
  eventId: string,
  expectedParticipants: number
): Promise<boolean> => {
  try {
    const participantsRef = collection(db, EVENTS_COLLECTION, eventId, 'participants');
    const snapshot = await getDocs(participantsRef);

    // We use >= just in case
    return snapshot.size >= expectedParticipants;
  } catch (error) {
    console.error('Error checking trigger:', error);
    return false;
  }
};

/**
 * Reopens an event's date range (host only, planning stage only).
 * C-130: runs through the `reopenEventDates` CF. The old client-side three-step
 * sequence could never complete — the availabilitySummary delete and the
 * other-participants flag resets are both denied by rules — leaving the event date
 * mutated while the stale summary survived. The CF commits all three writes in one
 * Admin-SDK batch (date expansion per M-336 happens server-side).
 */
export const reopenEventDates = async (
  eventId: string,
  newDateRange: { start: string; end: string }
): Promise<void> => {
  const fn = httpsCallable<
    { eventId: string; start: string; end: string },
    { success: boolean; dates: string[] }
  >(functions, 'reopenEventDates');
  await fn({ eventId, start: newDateRange.start, end: newDateRange.end });
};

/** H-95 / M-264: one-shot read returning the event plus access-check state. */
export const getEventById = async (
  eventId: string
): Promise<{ event: Event | null; found: boolean; permissionDenied: boolean }> => {
  try {
    const snap = await getDoc(doc(db, EVENTS_COLLECTION, eventId));
    if (!snap.exists()) return { event: null, found: false, permissionDenied: false };
    return {
      event: { id: snap.id, ...snap.data() } as Event,
      found: true,
      permissionDenied: false,
    };
  } catch (err: unknown) {
    const code = (err as { code?: string }).code;
    if (code === 'permission-denied') return { event: null, found: false, permissionDenied: true };
    throw err;
  }
};

/**
 * Subscribes to events where the user is host; calls back with raw event data
 * for notification processing. Returns an unsubscribe function.
 */
export const subscribeToNotifications = (
  userId: string,
  callback: (events: Array<{ id: string; name: string; status: string }>) => void,
  onError?: (error: FirestoreError) => void
): (() => void) => {
  const q = query(collection(db, EVENTS_COLLECTION), where('hostId', '==', userId));
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map((d) => ({
          id: d.id,
          name: (d.data().name as string) || 'Untitled Event',
          status: (d.data().status as string) ?? '',
        }))
      );
    },
    onError
  );
};

/** Result shape returned by the resolveVotingWinner CF. */
export interface ResolveWinnerResult {
  success: boolean;
  winnerId?: string;
  tie?: boolean;
  coWinners?: string[];
  reason?: 'no_votes';
}

/**
 * Resolve the venue winner server-side via the Schulze method over participants'
 * ranked ballots. The CF writes the winner (and transitions status) or persists a
 * tie's co-winners for the host to break; this wrapper just relays the outcome so
 * the caller can surface a no-votes prompt. Replaces the old client-side plurality
 * tally + persistVotingTie.
 */
export const resolveVotingWinner = async (eventId: string): Promise<ResolveWinnerResult> => {
  const fn = httpsCallable<{ eventId: string }, ResolveWinnerResult>(
    functions,
    'resolveVotingWinner'
  );
  const res = await fn({ eventId });
  return res.data;
};

/** C-31: call the resolveVotingTie CF — Admin SDK bypasses M-203 Firestore rule that blocks
 * client writes to `status`. CF determines nextStatus via server-side feature flag. */
export const resolveVotingTieBreaker = async (eventId: string, winnerId: string): Promise<void> => {
  const fn = httpsCallable(functions, 'resolveVotingTie');
  await fn({ eventId, winnerId });
};

export interface EventSummary {
  id: string;
  name: string;
  date: string;
  location?: string;
  /** Mirrors Event['status'] from @planacle/shared — the server never writes legacy aliases
   *  (draft/voting/confirmed/completed) so EventSummary uses the canonical union. */
  status: Event['status'];
  hostId: string;
  participantCount: number;
  eventType?: 'date' | 'hangout' | 'trip';
  /** H-318: cover chain inputs — leading candidate photo ref + vibes for the gradient fallback */
  coverPhotoRef?: string | null;
  vibe?: string[];
  /** M-368: capped first-name preview for the identity avatar pile on Events-list cards. */
  participantPreview?: { uid: string; name: string }[];
}

/** Map a raw event doc into an EventSummary plus a sort key (createdAt, newest first). */
const toEventRecord = (
  id: string,
  data: Record<string, unknown>
): { summary: EventSummary; sortMs: number } => {
  const createdAt = data.createdAt as { toMillis?: () => number } | Date | number | undefined;
  const sortMs =
    createdAt && typeof (createdAt as { toMillis?: () => number }).toMillis === 'function'
      ? (createdAt as { toMillis: () => number }).toMillis()
      : createdAt instanceof Date
        ? createdAt.getTime()
        : typeof createdAt === 'number'
          ? createdAt
          : 0;
  const participants = data.participants as Record<string, unknown> | undefined;
  return {
    sortMs,
    summary: {
      id,
      name: (data.name as string) || 'Untitled Event',
      date: (data.date as string) || (data.startDate as string) || new Date().toISOString(),
      location: (data.location as string) || 'TBD',
      status: (data.status as EventSummary['status']) || 'draft',
      hostId: data.hostId as string,
      // M-371: prefer the trigger-maintained count — the inline participants map never exists
      // (participants live in a subcollection), so the legacy derivation always returned 0.
      participantCount:
        (data.participantCount as number) ?? (participants ? Object.keys(participants).length : 0),
      eventType: (data.eventType as EventSummary['eventType']) || undefined,
      coverPhotoRef: (data.coverPhotoRef as string | null) ?? null,
      vibe: (data.vibe as string[]) || undefined,
      // M-368: capped first-name preview for the avatar pile (trigger-maintained).
      participantPreview: Array.isArray(data.participantPreview)
        ? (data.participantPreview as { uid: string; name: string }[])
        : undefined,
    },
  };
};

/**
 * Subscribes to every event the user belongs to — both events they **host** (`hostId == uid`)
 * and events they **joined as a guest/participant**. Guest-joined events live only in the
 * `events/{id}/participants/{uid}` subcollection under someone else's `hostId`, so the legacy
 * host-only query never surfaced them on Home / Events. We merge two live listeners:
 *
 *   A. `events where hostId == uid`            — hosted events (authoritative, always shown).
 *   B. `collectionGroup(participants).uid == uid` — membership; resolve each parent event doc.
 *
 * Results are de-duplicated by event id and sorted by createdAt (newest first). Listener B is
 * non-fatal: if it errors (index/rules), hosted events still render.
 */
export const subscribeToUserEvents = (
  userId: string,
  callback: (events: EventSummary[]) => void,
  onError?: (error: FirestoreError) => void
): (() => void) => {
  let hosted = new Map<string, { summary: EventSummary; sortMs: number }>();
  let joined = new Map<string, { summary: EventSummary; sortMs: number }>();
  let hostedLoaded = false;
  let joinedSeq = 0;

  const emit = () => {
    // Wait for the authoritative hosted list before first paint; joined merges in as it resolves.
    if (!hostedLoaded) return;
    const merged = new Map(joined);
    hosted.forEach((rec, id) => merged.set(id, rec)); // hosted view wins on overlap
    const list = [...merged.values()].sort((a, b) => b.sortMs - a.sortMs).map((r) => r.summary);
    callback(list);
  };

  // A — hosted events
  const unsubHosted = onSnapshot(
    query(
      collection(db, EVENTS_COLLECTION),
      where('hostId', '==', userId),
      orderBy('createdAt', 'desc')
    ),
    (snapshot) => {
      hosted = new Map(snapshot.docs.map((d) => [d.id, toEventRecord(d.id, d.data())]));
      hostedLoaded = true;
      emit();
    },
    onError
  );

  // B — events the user joined (participant membership across all events)
  const unsubJoined = onSnapshot(
    query(collectionGroup(db, 'participants'), where('uid', '==', userId)),
    async (snapshot) => {
      const mySeq = ++joinedSeq;
      const eventIds = Array.from(
        new Set(
          snapshot.docs
            .map((d) => d.ref.parent.parent?.id)
            .filter((id): id is string => typeof id === 'string')
        )
      );
      const records = await Promise.all(
        eventIds.map(async (id) => {
          try {
            const snap = await getDoc(doc(db, EVENTS_COLLECTION, id));
            return snap.exists() ? toEventRecord(snap.id, snap.data()) : null;
          } catch {
            return null; // event deleted or unreadable — skip silently
          }
        })
      );
      // Drop stale results if a newer participant snapshot arrived while we were fetching.
      if (mySeq !== joinedSeq) return;
      joined = new Map(
        records.filter((r): r is NonNullable<typeof r> => r !== null).map((r) => [r.summary.id, r])
      );
      emit();
    },
    (err) => {
      // Non-fatal — keep hosted events visible if membership lookup fails (e.g. missing index).
      console.error('[subscribeToUserEvents] joined-events listener error', err.code, err.message);
      joined = new Map();
      emit();
    }
  );

  return () => {
    unsubHosted();
    unsubJoined();
  };
};

/** C-32: call transitionEventStatus CF — Admin SDK bypasses M-203 Firestore rule that blocks
 * client writes to `status`. */
export const transitionEventStatus = async (
  eventId: string,
  toStatus: 'planning' | 'ready' | 'generating_complementary' | 'generating_itinerary',
  options?: {
    selectedCandidateIds?: string[];
    selectedComplementaryIds?: string[];
    votingDurationHours?: number;
    allowIncompleteGroup?: boolean;
  }
): Promise<void> => {
  const fn = httpsCallable(functions, 'transitionEventStatus');
  await fn({ eventId, toStatus, ...options });
};

/** H-290 / M-377: Notification shape stored in `users/{uid}/notifications`.
 * `type` discriminates between host nudges and activity-driven in-app notifications. */
export interface NudgeNotification {
  id: string;
  eventId: string;
  eventName: string;
  type: 'stuck_event_nudge' | 'activity_update';
  message: string;
  nudgeType?: '12h' | '24h';
  createdAt: Timestamp;
  read: boolean;
}

/** H-290: Subscribe to unread nudge notifications for a user. */
export const subscribeToNudges = (
  userId: string,
  callback: (nudges: NudgeNotification[]) => void,
  onError?: (error: FirestoreError) => void
): (() => void) => {
  const nudgesQuery = query(
    collection(db, 'users', userId, 'notifications'),
    where('read', '==', false)
  );
  return onSnapshot(
    nudgesQuery,
    (snapshot) => {
      const nudges: NudgeNotification[] = [];
      snapshot.forEach((d) => {
        nudges.push({ id: d.id, ...d.data() } as NudgeNotification);
      });
      callback(nudges);
    },
    onError
  );
};

/** H-290: Mark a single nudge notification as read. */
export const markNudgeRead = async (userId: string, nudgeId: string): Promise<void> => {
  const nudgeRef = doc(db, 'users', userId, 'notifications', nudgeId);
  await updateDoc(nudgeRef, { read: true });
};

/** H-290: Mark multiple nudge notifications as read in a single batch. */
export const markNudgesReadBatch = async (userId: string, nudgeIds: string[]): Promise<void> => {
  const batch = writeBatch(db);
  nudgeIds.forEach((id) => {
    const docRef = doc(db, 'users', userId, 'notifications', id);
    batch.update(docRef, { read: true });
  });
  await batch.commit();
};

/** M-423: Shape of a server-written removal notification. */
export interface RemovalNotification {
  type: 'removal';
  removalType: 'left' | 'kicked';
  eventId: string;
  createdAt: Timestamp;
}

/**
 * M-423: Subscribe to the server-written removal notification for a specific user+event pair.
 * The doc is written by leaveEvent/removeParticipant CFs after the participant doc is deleted,
 * so the client receives an authoritative signal instead of inferring removal from permission errors.
 * Returns an unsubscribe function.
 */
export const subscribeToRemovalNotification = (
  userId: string,
  eventId: string,
  onRemoved: (notification: RemovalNotification) => void,
  onError?: (error: FirestoreError) => void
): (() => void) => {
  const notifRef = doc(db, 'users', userId, 'notifications', `removal-${eventId}`);
  return onSnapshot(
    notifRef,
    (snapshot) => {
      if (snapshot.exists()) {
        onRemoved(snapshot.data() as RemovalNotification);
      }
    },
    onError
  );
};

/** M-423: Acknowledge (delete) the removal notification so it does not re-trigger on reconnect. */
export const acknowledgeRemovalNotification = async (
  userId: string,
  eventId: string
): Promise<void> => {
  const notifRef = doc(db, 'users', userId, 'notifications', `removal-${eventId}`);
  await deleteDoc(notifRef);
};
